import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Container} from 'src/design/atoms/Container';
import {Group} from 'src/design/atoms/Group';
import {List} from 'src/design/atoms/List';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconInfoCircle} from '@tabler/icons-react';
import {Link} from 'react-router-dom';

import {Button} from 'src/design/atoms/Button';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {resetUserPassword} from 'src/redux/slices/authSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const ForgotPasswordPage = () => {
  useSetDocumentTitle('Forgot Password');
  const navigate = useAppNavigate();
  const {username} = useAppSelector(state => state.auth);
  const dispatch = useAppDispatch();

  function handleSubmit(): void {
    if (!username) {
      return;
    }
    void dispatch(resetUserPassword(username));
    navigate(PathUtility.ResetPassword());
  }

  return (
    <Container size={450} my={40}>
      <Stack>
        <Title order={2} ta="center">
          Forgot your password?
        </Title>
        <Text c="dimmed" size="sm" ta="center">
          Enter your email and we will send you a reset code
        </Text>

        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack gap="xl">
            <Alert color="blue" title="What's next?" icon={<IconInfoCircle />}>
              <List size="sm" spacing="sm">
                <List.Item>
                  We will send you a code to reset your password.
                </List.Item>
                <List.Item>
                  If you don't receive it within 10 minutes, check your spam
                  folder.
                </List.Item>
                <List.Item>
                  If you need to change your email,{' '}
                  <Anchor
                    c="dimmed"
                    size="sm"
                    component={Link}
                    to={PathUtility.Login()}>
                    return to the login page. →
                  </Anchor>
                </List.Item>
              </List>
            </Alert>

            <Text c="dimmed" size="sm" ta="center">
              Your email:{' '}
              <Text span size="sm" c="blue">
                {username}
              </Text>
            </Text>
            <Button fullWidth onClick={handleSubmit}>
              Send Code
            </Button>
          </Stack>
        </Paper>
        <Group justify="space-between">
          <Anchor
            c="dimmed"
            size="sm"
            component={Link}
            to={PathUtility.Login()}>
            ← Back to login
          </Anchor>
          <Anchor
            c="dimmed"
            size="sm"
            component={Link}
            to={PathUtility.Support()}>
            Having trouble? →
          </Anchor>
        </Group>
      </Stack>
    </Container>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'transient',
  path: PathUtility.ForgotPassword(),
  element: <ForgotPasswordPage />,
  isPrivate: false,
});

export {ForgotPasswordPage};
